import { GatewayQuestionnaire } from "@conformite/gateway";
import {
  FormField,
  FormRow,
  InputWysiwyg,
  Popover,
  Tag,
} from "@endya-conformite/ui-kit";
import QuestionCircle from "@endya-conformite/ui-kit/src/assets/icons/question-circle.svg?react";
import Copy from "@endya-conformite/ui-kit/src/assets/icons/copy.svg?react";
import Check from "@endya-conformite/ui-kit/src/assets/icons/check.svg?react";
import { Button } from "@lya-protect/lya-protect-form-library/dist/UI";
import classNames from "classnames";
import { useState } from "react";
import { QuestionnaireColoredButtonGroup } from "../QuestionnaireColoredButtonGroup/QuestionnaireColoredButtonGroup";
import { getSuggestedTagVariant } from "./aiSuggestionColor";
import styles from "./QuestionnaireQuestion.module.scss";

function CommentField({
  label,
  helpText,
  disabled,
}: {
  label: string;
  helpText?: string;
  disabled?: boolean;
}) {
  return (
    <>
      <span className={styles.label}>{label}</span>
      {helpText && <p className={styles.helpText}>{helpText}</p>}
      <InputWysiwyg
        name="comment"
        placeholder="Commentaire"
        disabled={disabled}
      />
    </>
  );
}

const AI_INDETERMINATE_ANSWER = "INDETERMINE";
const AI_MANIPULATION_ANSWER = "MANIPULATION_SUSPECTEE";

export type AiAnswer = {
  answer: string;
  justification: string;
  evidence?: string;
  confidence?: "faible" | "moyenne" | "elevee";
  guardrail?: "PROMPT_LEAK";
  evidenceGrounded?: boolean;
};

function isSentinelAnswer(answer: string) {
  return (
    answer === AI_INDETERMINATE_ANSWER || answer === AI_MANIPULATION_ANSWER
  );
}

function AiManipulationAlert({ aiAnswer }: { aiAnswer: AiAnswer }) {
  return (
    <div role="alert" className={styles.aiManipulationAlert}>
      <span className={styles.aiManipulationTitle}>
        Tentative de manipulation suspectée dans les pièces
      </span>
      {aiAnswer.guardrail === "PROMPT_LEAK" ? (
        <span>
          Réponse neutralisée : la sortie de l’IA reprenait ses propres
          consignes.
        </span>
      ) : (
        aiAnswer.evidence && <span>Pièce concernée : {aiAnswer.evidence}</span>
      )}
    </div>
  );
}

function AiSuggestion({
  aiAnswer,
  options,
  colorOverrides,
}: {
  aiAnswer: AiAnswer;
  options: GatewayQuestionnaire.QuestionOption[];
  colorOverrides?: Record<string, GatewayQuestionnaire.OptionColor>;
}) {
  const [copied, setCopied] = useState(false);

  if (isSentinelAnswer(aiAnswer.answer)) return null;

  const tagVariant =
    getSuggestedTagVariant(aiAnswer.answer, options, colorOverrides) ?? "info";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(aiAnswer.justification);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Échec de la copie", err);
    }
  };

  return (
    <Popover
      icon={<QuestionCircle />}
      content="Pourquoi cette suggestion par IA"
      placement="bottom"
      variant="text"
      cardClassName={styles.aiSuggestionCard}
    >
      <div className={styles.aiSuggestion}>
        <div className={styles.title}>
          <div className={styles.suggestionLabel}>
            <span>Suggestion :</span>
            <Tag variant={tagVariant}>{aiAnswer.answer}</Tag>
          </div>
          {aiAnswer.justification && (
            <Button
              variant="text"
              icon={copied ? <Check /> : <Copy />}
              iconVariant="right"
              className={classNames(
                styles.copyButton,
                copied && styles.copyButtonDone
              )}
              onClick={handleCopy}
            >
              {copied ? "Copié !" : "Copier le texte"}
            </Button>
          )}
        </div>
        {(aiAnswer.justification || aiAnswer.evidence) && (
          <div className={styles.aiSuggestionBody}>
            {aiAnswer.justification && <p>{aiAnswer.justification}</p>}
            {aiAnswer.evidence && (
              <i
                className={classNames(
                  aiAnswer.evidenceGrounded === false &&
                    styles.evidenceUngrounded
                )}
              >
                «&nbsp;{aiAnswer.evidence}&nbsp;»
              </i>
            )}
            {aiAnswer.evidenceGrounded === false && (
              <span className={styles.evidenceUngroundedNotice}>
                Citation non retrouvée dans les pièces
              </span>
            )}
          </div>
        )}
      </div>
    </Popover>
  );
}

type QuestionnaireQuestionProps = {
  question: GatewayQuestionnaire.QuestionDefinitionResponse;
  disabled?: boolean;
  index?: number;
  aiAnswer?: AiAnswer;
  colorOverrides?: Record<string, GatewayQuestionnaire.OptionColor>;
};

export function QuestionnaireQuestion({
  question,
  disabled,
  index,
  aiAnswer,
  colorOverrides,
}: QuestionnaireQuestionProps) {
  const isComment = question.label === "Commentaire";

  return (
    <FormRow>
      <FormField>
        {index !== undefined && (
          <span className={styles.questionIndex}>Question {index + 1}</span>
        )}
        {isComment && (
          <CommentField
            label={question.label}
            helpText={question.helpText ?? undefined}
            disabled={disabled}
          />
        )}
        {!isComment && (
          <QuestionnaireColoredButtonGroup
            name={question.id}
            label={question.label}
            helpText={question.helpText ?? undefined}
            required={question.required}
            options={question.options}
            disabled={disabled}
            multiple={question.type === "multiple"}
            colorOverrides={colorOverrides}
            suggestedOptionLabel={
              aiAnswer && !isSentinelAnswer(aiAnswer.answer)
                ? aiAnswer.answer
                : undefined
            }
          />
        )}
        {!isComment && aiAnswer?.answer === AI_MANIPULATION_ANSWER && (
          <AiManipulationAlert aiAnswer={aiAnswer} />
        )}
        {!isComment && aiAnswer && (
          <AiSuggestion
            aiAnswer={aiAnswer}
            options={question.options}
            colorOverrides={colorOverrides}
          />
        )}
      </FormField>
    </FormRow>
  );
}
